import {useEffect, useState} from "react";
import Util from "../Util";
import {Alert, Button, ButtonGroup, Spinner, Table} from "react-bootstrap";
import ULPSPortalApiClient from "../UlpsPortalApiClient";
import Constant from "../Constant";
import LicenceRestrictions from "../components/LicenceRestrictions";
import store from "../store";
import {LicenceRestrictionsMode, Permission} from "../Enums";
import PermissionRequired from "../PermissionRequired";
import {useHistory} from "react-router-dom";
import LicenceFeatureOverview from "../components/LicenceFeatureOverview";

function isDelivery() {
  return store.session?.permissions?.indexOf(Permission.DELIVERY) !== -1;
}

export default function LicenceDetails({id}) {

  const history = useHistory();
  const [details, setDetails] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    ULPSPortalApiClient.getLicence(id).then(r => {
      setDetails(r);
    }).catch(_ => {
      setError(true);
    })
  }, [id]);

  useEffect(() => {
    if (details) {
      getLicenceStatus()
    }

  }, [details])

  const getLicenceStatus = (getSupport = false) => {
    const licenceOrSupportValidity = getSupport ? details.supportValidityDays : details.licenceValidityDays
    return !licenceOrSupportValidity ||
      (new Date(details.firstActivatedAt).getTime() + Util.dayToMs(licenceOrSupportValidity)) > new Date().getTime()
  }

const resetLicence = () => {
  if (
    window.confirm(
      "This will remove the current hardwareId, contact details from this licence, and the licence's status will be enabled.\nPress OK to continue."
    )
  ) {
    setLoading(true);
    setError(false);

    ULPSPortalApiClient.resetLicence(id)
      .then(() => ULPSPortalApiClient.getLicence(id))
      .then((r) => {
        ULPSPortalApiClient.enableLicence(id, details.hardwareIds);
        r.disabled=false;
        setDetails(r);
        setResetSuccess(true);
        window.scrollTo(0, 0);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }
};

  const enableLicence = () => {
    if (window.confirm("This action will enable the licence.\nPress OK to continue.")) {
      setLoading(true);
      ULPSPortalApiClient.enableLicence(id, details.hardwareIds)
        .then(() => ULPSPortalApiClient.getLicence(id))
        .then(r => {
          setDetails(r);
          window.scrollTo(0, 0);
        })
        .catch(err => {
          const message = err || "Licence could not be enabled.";
          window.alert(message);
        })
        .finally(() => setLoading(false));
    }
  };

  const disableLicence = () => {
    if (window.confirm("This will set the licence to a disabled mode.")) {
      setLoading(true);
      ULPSPortalApiClient.disableLicence(id)
        .then(() => ULPSPortalApiClient.getLicence(id))
        .then(r => {
          setDetails(r);
          window.scrollTo(0, 0);
        })
        .catch(_ => setError(true))
        .finally(() => setLoading(false));
    }
  };

  const downloadLicence = async () => {
    try {
      const data = await ULPSPortalApiClient.downloadSingeLicence(id)
      setError(null);
      Util.saveCsvFile(data);
    } catch (e) {
      setError(e)
    }
  }


  return <>
    <Button className={"floatyBackButton"} onClick={history.goBack}>Go back</Button>
    <h2>Licence</h2>

    {resetSuccess && <Alert variant="success">Licence has been successfully reset.</Alert>}
    
    {
      error ? <Alert variant="danger">Cannot get licence info</Alert> : details ? <>        
        <PermissionRequired requiredPermissions={[Permission.DELIVERY]} silent={true}>
          <ButtonGroup>
            <Button variant={details.disabled?"secondary":"success"} disabled={details.disabled} onClick={downloadLicence}>Download</Button>
            <Button variant={details.disabled?"secondary":"primary"} disabled={details.disabled} href={`#${Constant.url.EDIT_LICENCE}?id=${id}`}>Edit</Button>
            {
              details.firstActivatedAt &&
              <Button variant={"danger"} onClick={resetLicence}>Reset</Button>
            }
            {
              details.disabled && <Button variant={"outline-success"} onClick={enableLicence}>Enable</Button>
            }
            {
              !details.disabled && <Button variant={"outline-danger"} onClick={disableLicence}>Disable</Button>
            }
          </ButtonGroup>
        </PermissionRequired>
        
        <div className={"fixedCells"}>
          {details.disabled ? (
            <Alert variant="danger">
              ⚠️ This licence has been <strong>disabled</strong>.
            </Alert>
          ) : details.firstActivatedAt ? (
            <span className={"halfWidthAlert"}>
              {getLicenceStatus() ? (
                <Alert variant="success">Licence is active and valid.</Alert>
              ) : (
                <Alert variant="danger">Licence has expired.</Alert>
              )}
              {getLicenceStatus(true) ? (
                <Alert variant="success">Support is active.</Alert>
              ) : (
                <Alert variant="danger">Support has expired.</Alert>
              )}
            </span>
          ) : (
            <Alert variant="info">Licence is not activated yet.</Alert>
          )}
          <br/>
          <div className={details.disabled ? "disabled-content" : ""} style={{ position: "relative" }}>
            {loading && (
            <Spinner
              animation="border"
              style={{
                position: "absolute",
                top: -100,
                left: 0,
                right: 0,
                margin: "0 auto",
                zIndex: 10,
                display: "block"
              }}
            />
            )}
            <Table striped bordered>
              <thead>
              <tr>
                <th colSpan={2}>Key</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Activation Key</td>
                <td className={"monospace"}>{details.activationKey}</td>
              </tr>
              <tr>
                <td>Hardware Id on the activated device</td>
                <td className={details.hardwareIds?.length && "monospace"}>{details.hardwareIds?.join(",")
                  || "Not activated yet"}</td>
              </tr>
              {
                isDelivery() && <tr>
                  <td>Parent batch</td>
                  <td><a href={`#/batch/?distributorId=${details.distributorId}&batchNumber=${details.batchNumber}`}>
                    Batch {details.batchNumber || "unknown"}</a></td>
                </tr>
              }
              </tbody>
            </Table>

            <Table striped bordered>
              <thead>
              <tr>
                <th colSpan={2}>Customer Details</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Distributor</td>
                <td>
                  {
                    isDelivery() ? <a
                        href={`#${Constant.url.DISTRIBUTOR}/?id=${details.distributorId}`}>{details.distributorName || "unknown"}</a> :
                      details.distributorName || "unknown"
                  } ({details.distributorCountry || "Unknown country"})
                </td>
              </tr>
              <tr>
                <td>Purchase order ID</td>
                <td>{details.purchaseOrderId || "unknown"}</td>
              </tr>
              <tr>
                <td>Customer name</td>
                <td>{details.customerName || "unknown"}</td>
              </tr>
              <tr>
                <td>Contact Name</td>
                <td>{details.contactName || "unknown"}</td>
              </tr>
              <tr>
                <td>Contact Email</td>
                <td>{details.contactEmail || "unknown"}</td>
              </tr>
              </tbody>
            </Table>

            <LicenceRestrictions details={details} mode={LicenceRestrictionsMode.LICENCE}/>
            <LicenceFeatureOverview details={details}/>

            <Table striped bordered className={"w25"}>
              <thead>
              <tr>
                <th colSpan={4}>Licence Date</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Created</td>
                <td>{Util.formatDate(details.createdAt)}</td>
              </tr>
              <tr>
                <td>Updated</td>
                <td>{Util.formatDate(details.updatedAt)}</td>
              </tr>
              <tr>
                <td>First Activated</td>
                <td>{Util.formatDate(details.firstActivatedAt)}</td>
              </tr>
              <tr>
                <td>Last Activated</td>
                <td>{Util.formatDate(details.lastActivatedAt)}</td>
              </tr>
              </tbody>
            </Table>
          </div>
        </div>
      </> : <><br/><br/><Spinner animation="border"/></>
    }
  </>;
}
